import type { Job, JobKind, JobStatus } from '../api/types';

/**
 * Pure derivation of UI state from a durable `Job` (§4.4, §6.4). Kept free of
 * React so the mapping is unit-testable and shared by every job kind.
 */

export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = [
  'succeeded',
  'failed',
  'dead_letter',
  'cancelled',
];

const STATUS_LABELS: Record<JobStatus, string> = {
  queued: 'Queued',
  claimed: 'Queued',
  running: 'Running',
  retry_wait: 'Retrying',
  succeeded: 'Succeeded',
  failed: 'Failed',
  dead_letter: 'Failed',
  cancelled: 'Cancelled',
};

/** Job-kind-specific progress wording (summary §8.3 — not a generic spinner). */
const KIND_LABELS: Record<JobKind, string> = {
  game_import: 'Importing games',
  deviation_detection: 'Detecting deviations',
  engine_evaluation: 'Evaluating position',
  provider_sync: 'Syncing games',
  data_export: 'Exporting data',
  account_delete: 'Deleting account',
  canonical_gc: 'Maintaining positions',
};

export interface JobUiState {
  status: JobStatus | 'unknown';
  kind: JobKind | null;
  /** Human status line, kind-specific while in flight. */
  label: string;
  isTerminal: boolean;
  succeeded: boolean;
  failed: boolean;
  /** 0–100 when the job reports a finite total, else null. */
  progressPercent: number | null;
  /** "NN%" when a percent is known, else ''. */
  progressLabel: string;
}

export function deriveJobUi(job: Job | null): JobUiState {
  if (job === null) {
    return {
      status: 'unknown',
      kind: null,
      label: 'Pending…',
      isTerminal: false,
      succeeded: false,
      failed: false,
      progressPercent: null,
      progressLabel: '',
    };
  }

  const status = job.status;
  const isTerminal = (TERMINAL_JOB_STATUSES as readonly string[]).includes(status);
  const label = isTerminal
    ? STATUS_LABELS[status]
    : `${KIND_LABELS[job.kind]}…`;

  const completed = Number(job.progress.completed);
  const total = job.progress.total === null ? null : Number(job.progress.total);
  let progressPercent: number | null = null;
  if (total !== null && Number.isFinite(total) && total > 0 && Number.isFinite(completed)) {
    progressPercent = Math.round((completed / total) * 100);
    progressPercent = Math.min(100, Math.max(0, progressPercent));
  }

  return {
    status,
    kind: job.kind,
    label,
    isTerminal,
    succeeded: status === 'succeeded',
    failed: status === 'failed' || status === 'dead_letter',
    progressPercent,
    progressLabel: progressPercent === null ? '' : `${progressPercent}%`,
  };
}
